import React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import "../../../customStyles/bootstrap_table.css";
import CheckBox from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlank from "@material-ui/icons/CheckBoxOutlineBlank";
import { SERVER_URL } from "../../../api";

const { SearchBar, ClearSearchButton } = Search;

export const buttonsStyle = {
  maxWidth: "30px",
  maxHeight: "30px",
  minWidth: "30px",
  minHeight: "30px",
  marginLeft: "1em",
};

export function booleanFormatter(cell) {
  return cell ? (
    <CheckBox style={{ color: "#D91E48" }} />
  ) : (
    <CheckBoxOutlineBlank style={{ color: "#D91E48" }} />
  );
}

export function longTextFormatter(cell) {
  return (
    <td className="truncate">
      <div dangerouslySetInnerHTML={{ __html: cell }}></div>
    </td>
  );
}

function extractContent(s) {
  if (!s) return "----";
  var span = document.createElement("span");
  span.innerHTML = s;
  return span.textContent || span.innerText;
}

export function substringFormatter(cell) {
  const text = extractContent(cell);
  return text.length > 50 ? text.substring(0, 50) + "..." : text;
}

const buildDate = (date) => {
  if (Object.prototype.toString.call(date) !== "[object Date]") return;
  const displayDate = `${date.getDate()}/${date.getMonth() +
    1}/${date.getFullYear()}  ${date.getHours()}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
  return displayDate;
};
export function imageFormatter(imageURL) {
    if (!imageURL) return <div></div>;
  
  // If it's base64 (starts with data:image), use it directly
  if (imageURL.startsWith("data:image")) {
    return <img src={imageURL} width={50} alt="Preview"></img>;
  }
  
  // Otherwise, construct the full URL
  const imageSrc = imageURL.startsWith("/") 
    ? `${SERVER_URL}${imageURL}` 
    : `${SERVER_URL}/${imageURL}`;
  return <img src={imageSrc} width={50} alt="Preview"></img>;
}
export function dateFormatter(cell) {
  let date = new Date(cell);
  date = buildDate(date);

  return date;
}

const customTotal = (from, to, size) => (
  <span
    className="react-bootstrap-table-pagination-total"
    style={{ paddingLeft: "5px" }}
  >
    Showing {from}-{to} of {size} results
  </span>
);

const pagination = paginationFactory({
  page: 1,
  sizePerPage: 10,
  lastPageText: ">>",
  firstPageText: "<<",
  nextPageText: ">",
  prePageText: "<",
  showTotal: true,
  paginationTotalRenderer: customTotal,
  alwaysShowAllBtns: false,
  onPageChange: function(page, sizePerPage) {},
  onSizePerPageChange: function(page, sizePerPage) {},
});

const defaultRowClasses = (row) => (row.active === false ? "inactive-row" : "");

// Función para calcular el ancho automático de las columnas
const calculateColumnWidths = (data, columns) => {
  if (!data || data.length === 0) return columns;

  return columns.map((column) => {
    if (column.style && column.style.width) {
      // Si ya tiene un ancho definido, lo respetamos
      return column;
    }

    let maxWidth = 0;

    // Calcular ancho basado en el header (incluyendo espacio para iconos de sorting)
    const headerLength = column.text
      ? column.text.length
      : column.dataField
      ? column.dataField.length
      : 0;
    const sortIconsWidth = column.sort !== false ? 30 : 0; // Espacio adicional para los iconos de sorting
    maxWidth = Math.max(maxWidth, headerLength * 8 + 20 + sortIconsWidth); // 8px por caracter + padding + iconos

    // Calcular ancho basado en el contenido de las celdas
    data.forEach((row) => {
      const cellValue = row[column.dataField];
      let cellLength = 0;

      if (cellValue !== null && cellValue !== undefined) {
        if (typeof cellValue === "boolean") {
          cellLength = 3; // Para iconos de checkbox
        } else if (cellValue instanceof Date) {
          cellLength = 19; // Para fechas formateadas
        } else {
          const stringValue = extractContent(String(cellValue));
          cellLength = stringValue.length;
        }
      }

      const estimatedWidth = cellLength * 8 + 20; // 8px por caracter + padding
      maxWidth = Math.max(maxWidth, estimatedWidth);
    });

    // Establecer límites mínimos y máximos
    const minWidth = 80;
    const maxWidthLimit = 300;
    const finalWidth = Math.min(Math.max(maxWidth, minWidth), maxWidthLimit);

    return {
      ...column,
      style: {
        ...column.style,
        width: `${finalWidth}px`,
      },
      headerStyle: {
        ...column.headerStyle,
        width: `${finalWidth}px`,
        whiteSpace: "nowrap", // Evita que el título y los iconos se separen en líneas diferentes
        overflow: "visible", // Permite que los iconos de sorting sean visibles
      },
    };
  });
};

export default function Table({ data, columns, onSelectionChange, selected, allFilteredData, rowClasses: rowClassesProp, ...tableProps }) {
  const rowClasses = typeof rowClassesProp === "function" ? rowClassesProp : defaultRowClasses;
  // Calcular anchos automáticos para las columnas
  const autoSizedColumns = React.useMemo(() => {
    return calculateColumnWidths(data, columns);
  }, [data, columns]);

  // Estado local para las filas seleccionadas si no se proporciona externamente
  const [localSelected, setLocalSelected] = React.useState(selected || []);

  // Sincronizar con prop selected si se proporciona
  React.useEffect(() => {
    if (selected !== undefined) {
      setLocalSelected(selected);
    }
  }, [selected]);

  // Configuración de selección múltiple si se proporciona onSelectionChange
  const selectRow = onSelectionChange
    ? {
        mode: "checkbox",
        clickToSelect: true,
        selected: localSelected.map((row) => row.id),
        onSelect: (row, isSelect, rowIndex, e) => {
          let newSelected = [...localSelected];
          if (isSelect) {
            // Agregar fila si no está ya seleccionada
            if (!newSelected.find((r) => r.id === row.id)) {
              newSelected.push(row);
            }
          } else {
            // Remover fila
            newSelected = newSelected.filter((r) => r.id !== row.id);
          }
          setLocalSelected(newSelected);
          onSelectionChange(newSelected);
        },
        onSelectAll: (isSelect, rows, e) => {
          // Si se proporciona allFilteredData, usar todos los datos filtrados en lugar de solo los de la página actual
          const dataToSelect = allFilteredData && allFilteredData.length > 0 ? allFilteredData : rows;
          const newSelected = isSelect ? [...dataToSelect] : [];
          setLocalSelected(newSelected);
          onSelectionChange(newSelected);
        },
      }
    : undefined;

  return (
    <ToolkitProvider
      bootstrap4
      keyField="id"
      data={data}
      columns={autoSizedColumns}
      search
    >
      {(props) => (
        <div>
          <SearchBar placeholder="Search" {...props.searchProps} />
          <ClearSearchButton className="ml-3" {...props.searchProps} />
          <br />
          <br />
          <BootstrapTable
            wrapperClasses="table-responsive"
            classes="table table-head-custom table-vertical-center overflow-hidden auto-columns"
            bordered={false}
            // remote
            pagination={pagination}
            striped
            rowClasses={rowClasses}
            selectRow={selectRow}
            {...tableProps}
            {...props.baseProps}
          />
        </div>
      )}
    </ToolkitProvider>
  );
}
